/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * What DashboardNavbar renders under v2: a breadcrumb row only. The global
 * header already carries search, notifications, settings and the avatar, so
 * the 197 pages that render <DashboardNavbar /> need no edits.
 */

import { useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ChevronRight } from "lucide-react";
import routes from "routes";
import { v2 as t } from "styles/tokens/v2";
import { breadcrumbsFor, routeNameMap } from "./breadcrumbs";
import { useActiveApp } from "./apps";

export default function PageBreadcrumbs() {
  const { pathname } = useLocation();
  // Built on first render, not at import: routes.js imports every page, every
  // page imports DashboardNavbar, which imports this file, so at module
  // evaluation `routes` can still be undefined in that cycle.
  const { app } = useActiveApp();
  const labels = app?.labels;
  // The home dashboard is "Home" in the v2 sidebar; the crumb says the same.
  const routeNames = useMemo(() => routeNameMap(routes, { ...(labels || {}), dashboard: "Home" }), [labels]);
  const crumbs = breadcrumbsFor(pathname, routeNames);
  if (!crumbs.length) return null;
  return (
    <Box component="nav" aria-label="Breadcrumb" sx={{ mb: 1.5 }}>
      <Box component="ol" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, listStyle: "none", m: 0, p: 0 }}>
        {crumbs.map((c, i) => (
          <Box component="li" key={`${c.label}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {i > 0 && <ChevronRight size={14} color={t.text3} aria-hidden="true" />}
            {c.to ? (
              <Typography component={RouterLink} to={c.to} sx={{ fontSize: 13, color: t.text2, textDecoration: "none", "&:hover": { color: t.link, textDecoration: "underline" } }}>
                {c.label}
              </Typography>
            ) : (
              <Typography component="span" aria-current={i === crumbs.length - 1 ? "page" : undefined} sx={{ fontSize: 13, color: t.text, fontWeight: 500 }}>
                {c.label}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
